import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { visuallyHidden } from "@mui/utils";
import { Stepper, Step, StepIndicator } from "@financeable-com-au/financeable-ui";

export interface ProgressStep {
  key: string;
  label: string;
  isCompleted?: boolean;
}

interface ProgressStepperProps {
  steps: ProgressStep[];
  /** The step in progress. Any other step shows as completed or still to come. */
  activeStep: string;
}

/** Read after a completed step's label, as a screen reader cannot see the tick. */
const COMPLETED_LABEL = ", completed";

/**
 * A modal's steps in a row: ticked once completed, filled while in progress, outlined until reached. The step in
 * progress is the list's `aria-current` step, so assistive technology knows where the user is too.
 */
export const ProgressStepper = ({ steps, activeStep }: ProgressStepperProps) => (
  <Stepper width="100%">
    {steps.map(({ key, label, isCompleted }, index) => {
      const isActive = key === activeStep;
      return (
        <Step
          key={key}
          aria-current={isActive ? "step" : undefined}
          indicator={
            isCompleted ? (
              <StepIndicator variant="soft" color="success" data-testid="step-indicator-complete">
                <CheckRoundedIcon sx={{ fontSize: "1rem" }} />
              </StepIndicator>
            ) : (
              <StepIndicator
                variant={isActive ? "solid" : "outlined"}
                data-testid={`step-indicator-${isActive ? "active" : "incomplete"}`}
                color="primary"
              >
                {index + 1}
              </StepIndicator>
            )
          }
        >
          {label}
          {isCompleted && <span style={visuallyHidden}>{COMPLETED_LABEL}</span>}
        </Step>
      );
    })}
  </Stepper>
);
